import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { SnackContext } from 'src/store/ContextStore';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import useDateFormatter from 'src/hooks/useDateFormatter';
import TextInput from 'src/components/UI/input/TextInput';
import DateInput from 'src/components/UI/input/DateInput';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import UnsavedChangesDialog from 'src/components/UI/dialog/UnsavedChangesDialog';
import {
  ChangedField,
  UnsavedChangeCount,
  useChangedFields,
  useUnsavedFormGuard
} from 'src/components/UI/input/changed-field';
import { toStoredDate } from 'src/util/util';
import { toLocalDate, toLocalDateFromInstant } from 'src/util/partialDate';
import Qrisk3StoredResult, {
  FamilyHistoryEvidence
} from './Qrisk3StoredResult';
import Qrisk3Disclaimer from './Qrisk3Disclaimer';
import {
  CONDITION_FIELDS,
  DIABETES_OPTIONS,
  ETHNICITY_OPTIONS,
  FEATURE_TITLE,
  INPUT_LABELS,
  MODEL_NAME,
  SMOKING_CODE_OPTIONS,
  entryReasonText,
  noteText,
  qrisk3ProblemText,
  sourceText
} from './qrisk3Text';
import {
  MEASUREMENT_PROBLEM_CODES,
  measurementProblemText
} from './measurementsText';
import {
  applyDraft,
  bmiOfForm,
  draftPayloadOf,
  formFromPrefill,
  isFromDraft,
  isPrefilled,
  payloadOf,
  typedReadings
} from './qrisk3Form';

const SEX_OPTIONS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' }
];

// The family history answer is yes, no or not yet answered (null). Read as text
// so that "not answered" and "no" are different values, which as flags they
// would not be.
const familyHistoryText = (v) => (typeof v === 'boolean' ? String(v) : '');

// Every field on screen, in screen order: what is drawn gold, counted, and
// jumped to. The confirmation that QRISK3 applies is an attestation given at the
// calculation, not a value, so it is none of them.
const FORM_FIELDS = [
  'sex',
  'age',
  'ethnicity',
  'systolicBp',
  'systolicBpSd',
  'cholesterolHdlRatio',
  'smoking',
  'heightCm',
  'weightKg',
  'bmi',
  'townsend',
  'townsendNotKnown',
  'measuredOn',
  {
    name: 'familyHistoryChd',
    read: (v) => familyHistoryText(v.familyHistoryChd),
    compare: { kind: 'text' }
  },
  'diabetes',
  ...CONDITION_FIELDS.map((c) => c.key)
];
const FIELD_NAMES = FORM_FIELDS.map((f) =>
  typeof f === 'string' ? f : f.name
);
const COMPARE = {
  measuredOn: { kind: 'date' },
  townsendNotKnown: { kind: 'flag' },
  ...Object.fromEntries(CONDITION_FIELDS.map((c) => [c.key, { kind: 'flag' }]))
};

// Measurement problems name measurement fields; the form's own are these.
const MEASUREMENT_TO_FORM = {
  systolicBp: 'systolicBp',
  cholesterolHdlRatio: 'cholesterolHdlRatio',
  heightCm: 'heightCm',
  weightKg: 'weightKg',
  smokingStatus: 'smoking',
  townsendScore: 'townsend',
  measuredOn: 'measuredOn'
};

// A select holds its value as text; its options hold numbers.
const labelOf = (options, value) =>
  options.find((o) => String(o.value) === String(value))?.label ?? null;

// A section of the form: a real heading, with a rule above every section but
// the first. Set as the app's section labels are (small capitals, muted), so it
// never reads as one more field label: the labels beneath it are bold too.
function Section({ title, first = false, children }) {
  return (
    <Box component="section" sx={{ mt: first ? 2 : 3 }}>
      {!first && <Divider sx={{ mb: 1.5 }} />}
      <Typography
        component="h3"
        sx={{
          fontSize: '.75rem',
          fontWeight: 700,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
          color: 'text.secondary',
          mb: 1.5
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Caption({ error = false, children, sx }) {
  if (!children) return null;
  return (
    <Typography
      variant="caption"
      component="div"
      sx={{ color: error ? 'error.main' : 'text.secondary', ...sx }}
    >
      {children}
    </Typography>
  );
}

/**
 * QRISK3 10-year cardiovascular risk for one person (TGCLOUD-1911).
 *
 * Opens on what the record already says (GET /qrisk3/inputs), each input with
 * where it came from, and on the person's saved draft if there is one; the
 * clinician checks and completes them. Calculate posts them, and the server
 * checks every one, records any readings typed in as clinical measurements, and
 * stores the result, shown with ClinRisk's disclaimer beneath it. Save draft
 * keeps what is there when not everything is known yet: the readings are
 * recorded as clinical measurements at once, each on its own day, and the other
 * answers wait for the calculation (services/qrisk3/draft.js).
 *
 * Changes are drawn as every clinician edit screen draws them (TGCLOUD-1902),
 * against the values the form opened on: the record's and the draft's.
 */
export default function Qrisk3Modal({
  open,
  onClose,
  userId,
  familyMemberId,
  memberRecords,
  anonymous = true,
  onCalculated,
  onSaved
}) {
  const t = useAutoTranslation();
  const fmtDate = useDateFormatter();
  const { setSnack } = useContext(SnackContext);
  const [status, setStatus] = useState('idle');
  const [prefill, setPrefill] = useState(null);
  const [form, setForm] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [loadKey, setLoadKey] = useState(0);
  const [problems, setProblems] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(null);
  const [savedSomething, setSavedSomething] = useState(false);
  const [askToLeave, setAskToLeave] = useState(false);

  // The record's values on the clinician's own day, with the saved draft over them.
  const load = useCallback(() => {
    const today = toStoredDate(new Date());
    return axios
      .get(APICONSTANTS.qrisk3Inputs.replace(':user_id', userId), {
        params: { familyMemberId, onDay: today }
      })
      .then(({ data }) => {
        const start = applyDraft(formFromPrefill(data, today), data.draft);
        setPrefill(data);
        setForm(start);
        setBaseline(start);
        setLoadKey((k) => k + 1);
        setStatus('ready');
      });
  }, [userId, familyMemberId]);

  useEffect(() => {
    if (!open || !userId) return;
    setStatus('loading');
    setResult(null);
    setProblems([]);
    setSavedSomething(false);
    load().catch((err) => {
      setStatus(err?.response?.data?.code === 'QRISK3_OFF' ? 'off' : 'error');
    });
  }, [open, userId, load]);

  const bmi = form ? bmiOfForm(form) : null;
  const changes = useChangedFields({
    baseline: baseline || {},
    values: form || {},
    mode: 'edit',
    fields: FORM_FIELDS,
    compare: COMPARE,
    // A BMI worked out from height and weight is the system's, never a change.
    exclude: (name) => name === 'bmi' && bmi !== null,
    resetKey: loadKey
  });
  const unsaved = result ? 0 : changes.unsavedCount;
  useUnsavedFormGuard(open ? unsaved : 0);

  const typed = useMemo(
    () => (form && prefill ? typedReadings(form, prefill) : []),
    [form, prefill]
  );
  const notApplicable = prefill?.applicability || [];
  const draft = prefill?.draft || null;

  const set = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setProblems((prev) =>
      prev.filter(
        (p) =>
          p.field && p.field !== name && MEASUREMENT_TO_FORM[p.field] !== name
      )
    );
  };
  const handleChange = (e) => set(e.target.name, e.target.value);

  const problemFor = (field) =>
    problems.find(
      (p) => p.field === field || MEASUREMENT_TO_FORM[p.field] === field
    );
  // A typed reading is checked as a measurement; everything else as an input.
  const problemWords = (p) =>
    MEASUREMENT_PROBLEM_CODES.includes(p.code)
      ? measurementProblemText(p, t)
      : qrisk3ProblemText(p, t);
  // When the draft was saved: an instant, read on the clinician's own day.
  const draftDay = draft ? fmtDate(toLocalDateFromInstant(draft.savedAt)) : '';
  const draftWords = draft
    ? t('Saved in the draft on {{date}}', { date: draftDay })
    : '';
  // Where a value came from, or why it was refused.
  const helper = (field) => {
    const p = problemFor(field);
    if (p) return problemWords(p);
    if (!form || !prefill) return undefined;
    if (isFromDraft(form, prefill, field)) return draftWords;
    if (isPrefilled(form, prefill, field))
      return sourceText(prefill.inputs[field], t, fmtDate);
    return typed.includes(field)
      ? t('Entered: recorded as a measurement')
      : undefined;
  };

  const close = () => {
    if (unsaved > 0) return setAskToLeave(true);
    return onClose(Boolean(result) || savedSomething);
  };

  const refused = (err) => {
    const listed = err?.response?.data?.problems;
    setProblems(
      Array.isArray(listed) && listed.length
        ? listed
        : [{ field: null, code: 'UNKNOWN' }]
    );
  };

  const calculate = () => {
    setBusy('calculate');
    setProblems([]);
    return axios
      .post(APICONSTANTS.qrisk3Calculate.replace(':user_id', userId), {
        familyMemberId,
        onDay: prefill?.onDay,
        inputs: payloadOf(form)
      })
      .then(({ data }) => {
        setResult(data.qrisk3Result);
        onCalculated?.(data);
      })
      .catch(refused)
      .finally(() => setBusy(null));
  };

  const saveDraft = () => {
    setBusy('draft');
    setProblems([]);
    return axios
      .put(APICONSTANTS.qrisk3Draft.replace(':user_id', userId), {
        familyMemberId,
        onDay: prefill?.onDay,
        inputs: draftPayloadOf(form)
      })
      .then(({ data }) => {
        setSavedSomething(true);
        const readings = Boolean(data?.measurementsRecordId);
        setSnack?.({
          status: true,
          severity: 'success',
          msg:
            readings && data?.draft
              ? t(
                  'Saved. The readings entered were recorded as clinical measurements, and the other answers are kept until the risk is calculated.'
                )
              : readings
              ? t(
                  'Saved. The readings entered were recorded as clinical measurements.'
                )
              : t('Saved. The answers are kept until the risk is calculated.')
        });
        onSaved?.(data);
        // Open again on what was saved: the readings now come from the record.
        return load().catch(() => setStatus('error'));
      })
      .catch(refused)
      .finally(() => setBusy(null));
  };

  const discardDraft = () => {
    setBusy('discard');
    return axios
      .delete(APICONSTANTS.qrisk3Draft.replace(':user_id', userId), {
        params: { familyMemberId }
      })
      .then(() => load())
      .catch(() =>
        setSnack?.({
          status: true,
          severity: 'error',
          msg: t('The draft could not be discarded.')
        })
      )
      .finally(() => setBusy(null));
  };

  // One outlined input in a grid cell, drawn gold when it differs from what the
  // form opened on.
  const inputCell = (field, size, input, extra = {}) => (
    <ChangedField
      component={Grid}
      size={size}
      name={field}
      changed={changes.changed[field]}
      error={Boolean(problemFor(field))}
      {...extra}
    >
      {input}
    </ChangedField>
  );
  const numberField = (field, size = { xs: 12, sm: 6 }, helperText) =>
    inputCell(
      field,
      size,
      <TextInput
        label={INPUT_LABELS[field]}
        type="number"
        name={field}
        value={form[field]}
        onChange={handleChange}
        error={Boolean(problemFor(field))}
        helperText={helperText ?? helper(field)}
      />
    );
  // A select draws its own helper only in error, and then only as a string it
  // translates again (SelectInput), so the source or the problem goes beneath it
  // here, where a TextField would draw its helper.
  const selectField = (field, options, size, helperText) => {
    const problem = problemFor(field);
    return inputCell(
      field,
      size,
      <>
        <TextInput
          label={INPUT_LABELS[field]}
          type="select"
          name={field}
          value={form[field]}
          options={options}
          onChange={handleChange}
        />
        <Caption error={Boolean(problem)} sx={{ mt: '3px', mx: '14px' }}>
          {problem ? problemWords(problem) : helperText}
        </Caption>
      </>,
      {
        formatWas: (v) => (labelOf(options, v) ? t(labelOf(options, v)) : null)
      }
    );
  };

  const fh = prefill?.inputs?.familyHistoryChd;
  const general = problems.filter(
    (p) =>
      !p.field ||
      !(FIELD_NAMES.includes(p.field) || MEASUREMENT_TO_FORM[p.field])
  );
  const showForm =
    status === 'ready' && !result && !notApplicable.length && form;

  const ethnicityHelp = () => {
    const recorded = prefill.inputs.ethnicity?.recorded || [];
    const source = helper('ethnicity');
    const values = recorded.length
      ? t('Recorded: {{values}}', { values: recorded.join(', ') })
      : null;
    if (!source && !values) return undefined;
    return (
      <>
        {source}
        {source && values && <br />}
        {values}
      </>
    );
  };
  const smokingHelp = () => {
    const hint = noteText(prefill.inputs.smoking?.hint, t);
    const source = helper('smoking');
    if (!source && !hint) return undefined;
    return (
      <>
        {source}
        {source && hint && <br />}
        {hint}
      </>
    );
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        {t('{{title}} ({{model}})', {
          title: t(FEATURE_TITLE),
          model: MODEL_NAME
        })}
        <IconButton
          aria-label={t('close')}
          onClick={close}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'inherit' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {status === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {status === 'off' && (
          <Alert severity="info">
            {t(
              'Cardiovascular risk is not switched on for this family’s group.'
            )}
          </Alert>
        )}
        {status === 'error' && (
          <Alert severity="error">
            {t('The inputs could not be read. Try again.')}
          </Alert>
        )}
        {status === 'ready' && result && (
          <Qrisk3StoredResult
            result={result}
            memberRecords={memberRecords}
            anonymous={anonymous}
          />
        )}
        {status === 'ready' && !result && notApplicable.length > 0 && (
          <Alert severity="warning">
            {notApplicable.map((p) => (
              <div key={p.code}>{qrisk3ProblemText(p, t)}</div>
            ))}
          </Alert>
        )}
        {showForm && (
          <Box>
            {draft && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={discardDraft}
                    disabled={Boolean(busy)}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {t('Discard draft')}
                  </Button>
                }
              >
                {t(
                  'Carrying on from the draft saved on {{date}}. Answers from the draft say so; everything else is read from the record again.',
                  { date: draftDay }
                )}
              </Alert>
            )}
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t(
                'Values are taken from the record where it holds them, and each says where it came from. Check every one: the score is only as good as its inputs.'
              )}
            </Typography>

            <Section first title={t('Person')}>
              <Grid container spacing={2}>
                {selectField(
                  'sex',
                  SEX_OPTIONS,
                  { xs: 12, sm: 4 },
                  helper('sex') || noteText(prefill.inputs.sex?.note, t)
                )}
                {numberField('age', { xs: 12, sm: 4 })}
                {selectField(
                  'ethnicity',
                  ETHNICITY_OPTIONS,
                  { xs: 12, sm: 4 },
                  ethnicityHelp()
                )}
              </Grid>
              {(prefill.inputs.age?.notes || []).map((n) => (
                <Caption key={n} sx={{ mt: 1 }}>
                  {noteText(n, t)}
                </Caption>
              ))}
            </Section>

            <Section title={t('Measurements and smoking')}>
              <Grid container spacing={2}>
                {numberField('systolicBp')}
                {numberField(
                  'systolicBpSd',
                  { xs: 12, sm: 6 },
                  helper('systolicBpSd') ||
                    t(
                      'The standard deviation of at least two readings from the last five years'
                    )
                )}
                {numberField('cholesterolHdlRatio')}
                {selectField(
                  'smoking',
                  SMOKING_CODE_OPTIONS,
                  { xs: 12, sm: 6 },
                  smokingHelp()
                )}
                {numberField('heightCm', { xs: 12, sm: 4 })}
                {numberField('weightKg', { xs: 12, sm: 4 })}
                {bmi !== null ? (
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextInput
                      label={INPUT_LABELS.bmi}
                      name="bmiWorkedOut"
                      value={bmi.toFixed(1)}
                      disabled
                      error={Boolean(problemFor('bmi'))}
                      helperText={
                        problemFor('bmi')
                          ? problemWords(problemFor('bmi'))
                          : t('From height and weight')
                      }
                    />
                  </Grid>
                ) : (
                  numberField(
                    'bmi',
                    { xs: 12, sm: 4 },
                    problemFor('bmi')
                      ? problemWords(problemFor('bmi'))
                      : isFromDraft(form, prefill, 'bmi')
                      ? draftWords
                      : t('Or enter height and weight')
                  )
                )}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <ChangedField
                    name="townsend"
                    changed={changes.changed.townsend}
                    error={Boolean(problemFor('townsend'))}
                  >
                    <TextInput
                      label={INPUT_LABELS.townsend}
                      type="number"
                      name="townsend"
                      value={form.townsendNotKnown ? '' : form.townsend}
                      onChange={handleChange}
                      disabled={form.townsendNotKnown}
                      error={Boolean(problemFor('townsend'))}
                      helperText={helper('townsend')}
                    />
                  </ChangedField>
                  <ChangedField
                    name="townsendNotKnown"
                    variant="control"
                    changed={changes.changed.townsendNotKnown}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={form.townsendNotKnown}
                          onChange={(e) =>
                            set('townsendNotKnown', e.target.checked)
                          }
                        />
                      }
                      label={t(
                        'Not known: calculate with 0, the national average'
                      )}
                    />
                  </ChangedField>
                  {isFromDraft(form, prefill, 'townsendNotKnown') && (
                    <Caption>{draftWords}</Caption>
                  )}
                </Grid>
                {typed.length > 0 &&
                  inputCell(
                    'measuredOn',
                    { xs: 12, sm: 6 },
                    <>
                      <FormLabel>
                        Readings entered here were measured on
                      </FormLabel>
                      <DateInput
                        liveText
                        value={
                          typeof form.measuredOn === 'string'
                            ? toLocalDate(form.measuredOn)
                            : form.measuredOn
                        }
                        disableFuture
                        onChange={(val) =>
                          set('measuredOn', toStoredDate(val) || '')
                        }
                        variant="outline"
                        error={Boolean(problemFor('measuredOn'))}
                        helperText={
                          problemFor('measuredOn')
                            ? problemWords(problemFor('measuredOn'))
                            : t(
                                'They are recorded as this person’s clinical measurements.'
                              )
                        }
                      />
                    </>,
                    { kind: 'date' }
                  )}
              </Grid>
            </Section>

            <Section title={t('Family history')}>
              <FormLabel>
                Angina or heart attack in a first-degree relative under 60
              </FormLabel>
              <ChangedField
                name="familyHistoryChd"
                variant="control"
                changed={changes.changed.familyHistoryChd}
                formatWas={(v) =>
                  v === 'true' ? t('Yes') : v === 'false' ? t('No') : null
                }
              >
                <RadioGroup
                  row
                  name="familyHistoryChd"
                  value={familyHistoryText(form.familyHistoryChd)}
                  onChange={(e) =>
                    set('familyHistoryChd', e.target.value === 'true')
                  }
                >
                  <FormControlLabel
                    value="true"
                    control={<Radio size="small" />}
                    label={t('Yes')}
                  />
                  <FormControlLabel
                    value="false"
                    control={<Radio size="small" />}
                    label={t('No')}
                  />
                </RadioGroup>
              </ChangedField>
              <Caption error={Boolean(problemFor('familyHistoryChd'))}>
                {problemFor('familyHistoryChd')
                  ? problemWords(problemFor('familyHistoryChd'))
                  : isFromDraft(form, prefill, 'familyHistoryChd')
                  ? draftWords
                  : fh?.value === null
                  ? t(
                      'The pedigree cannot answer this: check the relatives below and choose.'
                    )
                  : isPrefilled(form, prefill, 'familyHistoryChd')
                  ? t('From the pedigree')
                  : t('Entered')}
              </Caption>
              {(fh?.notes || []).map((n) => (
                <Caption key={n}>{noteText(n, t)}</Caption>
              ))}
              <FamilyHistoryEvidence
                relatives={fh?.relatives}
                memberRecords={memberRecords}
                anonymous={anonymous}
              />
            </Section>

            <Section title={t('Conditions')}>
              <FormLabel>Diabetes</FormLabel>
              <ChangedField
                name="diabetes"
                variant="control"
                changed={changes.changed.diabetes}
                options={DIABETES_OPTIONS}
              >
                <RadioGroup
                  row
                  name="diabetes"
                  value={form.diabetes}
                  onChange={handleChange}
                >
                  {DIABETES_OPTIONS.map((o) => (
                    <FormControlLabel
                      key={o.value}
                      value={o.value}
                      control={<Radio size="small" />}
                      label={t(o.label)}
                    />
                  ))}
                </RadioGroup>
              </ChangedField>
              <Caption error={Boolean(problemFor('diabetes'))}>
                {problemFor('diabetes')
                  ? problemWords(problemFor('diabetes'))
                  : isFromDraft(form, prefill, 'diabetes')
                  ? draftWords
                  : noteText(prefill.inputs.diabetes?.note, t) ||
                    (isPrefilled(form, prefill, 'diabetes')
                      ? sourceText(prefill.inputs.diabetes, t, fmtDate)
                      : '')}
              </Caption>
              {CONDITION_FIELDS.filter(
                (c) => !c.maleOnly || form.sex === 'male'
              ).map((c) => {
                const pre = prefill.inputs[c.key];
                const recorded = pre?.evidence?.counted || [];
                const listed = pre?.evidence?.listed || [];
                const hinted = pre?.hint?.counted || [];
                return (
                  <Box key={c.key} sx={{ mt: 1 }}>
                    <ChangedField
                      name={c.key}
                      variant="control"
                      changed={changes.changed[c.key]}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={form[c.key] === true}
                            onChange={(e) => set(c.key, e.target.checked)}
                          />
                        }
                        label={t(c.label)}
                      />
                    </ChangedField>
                    <Box sx={{ ml: 4 }}>
                      <Caption>{t(c.definition)}</Caption>
                      {isFromDraft(form, prefill, c.key) && (
                        <Caption>{draftWords}</Caption>
                      )}
                      {recorded.map((e, i) => (
                        <Caption key={`r${i}`} sx={{ color: 'text.primary' }}>
                          {t('Recorded: {{condition}}', {
                            condition: e.name || e.codeId
                          })}
                        </Caption>
                      ))}
                      {listed.map((e, i) => (
                        <Caption key={`l${i}`} sx={{ color: 'text.primary' }}>
                          {t(
                            'Also on the record, not counted ({{reason}}): {{condition}}',
                            {
                              reason: entryReasonText(e.reason, t),
                              condition: e.name || e.codeId
                            }
                          )}
                        </Caption>
                      ))}
                      {hinted.map((e, i) => (
                        <Caption key={`h${i}`} sx={{ color: 'text.primary' }}>
                          {t(
                            'On the record: {{condition}}. Tick only if this applies now.',
                            { condition: e.name || e.codeId }
                          )}
                        </Caption>
                      ))}
                    </Box>
                  </Box>
                );
              })}
              {prefill.engine?.conceptSetsReview && (
                <Caption sx={{ mt: 1 }}>
                  {t(
                    'Conditions are read from the record with code sets that are awaiting clinical review.'
                  )}
                </Caption>
              )}
            </Section>

            <Divider sx={{ my: 2 }} />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={form.confirmedNoCvdNoStatin}
                  onChange={(e) =>
                    set('confirmedNoCvdNoStatin', e.target.checked)
                  }
                />
              }
              label={t(
                'This person has no cardiovascular disease (coronary heart disease, stroke or TIA) and is not taking a statin. QRISK3 does not apply otherwise.'
              )}
            />
            {problemFor('confirmedNoCvdNoStatin') && (
              <Caption error>
                {problemWords(problemFor('confirmedNoCvdNoStatin'))}
              </Caption>
            )}
            {problems.length > 0 && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {(general.length ? general : problems).map((p, i) => (
                  <div key={`${p.code}-${i}`}>{problemWords(p)}</div>
                ))}
              </Alert>
            )}
            <Qrisk3Disclaimer />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3 }}>
        {showForm && <UnsavedChangeCount count={changes.count} />}
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" onClick={close}>
          {t('Close')}
        </Button>
        {showForm && (
          <>
            <Button
              variant="outlined"
              color="secondary"
              onClick={saveDraft}
              disabled={Boolean(busy) || unsaved === 0}
            >
              {busy === 'draft' ? t('Saving…') : t('Save draft')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={calculate}
              disabled={Boolean(busy)}
            >
              {busy === 'calculate'
                ? t('Calculating…')
                : t('Calculate and record')}
            </Button>
          </>
        )}
      </DialogActions>
      <UnsavedChangesDialog
        open={askToLeave}
        count={unsaved}
        onCancel={() => setAskToLeave(false)}
        onDiscard={() => {
          setAskToLeave(false);
          onClose(savedSomething);
        }}
      />
    </Dialog>
  );
}
